"use client"

import { useState, useContext, useEffect } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  InputAdornment,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import CreditCardIcon from "@mui/icons-material/CreditCard"
import PaymentIcon from "@mui/icons-material/Payment"
import SaveIcon from "@mui/icons-material/Save"
import CloseIcon from "@mui/icons-material/Close"
import SearchIcon from "@mui/icons-material/Search"

const TablaMedioDePagoComponent = () => {
  const {
    medioDePagoConDescripcion,
    getMedioDePagoConDescripcion,
    tipoMedioDePago,
    getTipoMedioDePago,
    updateMedioDePago,
    deleteMedioDePago,
  } = useContext(ErpContext)
  
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [actionLoadingId, setActionLoadingId] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  
  // Estados para el modal de edición
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editingMedio, setEditingMedio] = useState(null)
  const [editDescripcion, setEditDescripcion] = useState("")
  const [editIdTipoMedioDePago, setEditIdTipoMedioDePago] = useState("")

  useEffect(() => {
    if (getMedioDePagoConDescripcion) {
      getMedioDePagoConDescripcion()
    }
    if (getTipoMedioDePago) {
      getTipoMedioDePago()
    }
  }, [])

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const getTipoDescripcion = (idTipo) => {
    const tipo = tipoMedioDePago.find((t) => t.id == idTipo)
    return tipo ? tipo.descripcion : "Sin tipo"
  }

  // Filtrar medios basándose en el término de búsqueda
  const filteredData = medioDePagoConDescripcion.filter((medio) => {
    if (!searchTerm.trim()) return true
    
    const searchLower = searchTerm.toLowerCase()
    const descripcion = medio.descripcion?.toLowerCase() || ""
    const tipoDesc = getTipoDescripcion(medio.idTipoMedioDePago)?.toLowerCase() || ""
    const id = medio.id?.toString() || ""
    
    return (
      descripcion.includes(searchLower) ||
      tipoDesc.includes(searchLower) ||
      id.includes(searchLower)
    )
  })

  // Aplicar paginación a los datos filtrados
  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  // Resetear página cuando cambia el término de búsqueda
  useEffect(() => {
    setPage(0)
  }, [searchTerm])

  const handleOpenEditModal = (medio) => {
    setActionError(null)
    setEditingMedio(medio)
    setEditDescripcion(medio.descripcion)
    setEditIdTipoMedioDePago(medio.idTipoMedioDePago)
    setEditModalOpen(true)
  }

  const handleCloseEditModal = () => {
    setEditModalOpen(false)
    setEditingMedio(null)
    setEditDescripcion("")
    setEditIdTipoMedioDePago("")
  }

  const handleSaveEdit = async () => {
    if (!editDescripcion.trim()) {
      setActionError("Por favor ingrese una descripción para el medio de pago")
      return
    }

    if (!editIdTipoMedioDePago) {
      setActionError("Por favor seleccione un tipo de medio de pago")
      return
    }

    setActionLoadingId(editingMedio.id)
    try {
      await updateMedioDePago(editingMedio.id, {
        descripcion: editDescripcion.trim(),
        idTipoMedioDePago: editIdTipoMedioDePago,
      })
      handleCloseEditModal()
      setActionError(null)
    } catch (error) {
      console.error("Error al actualizar medio:", error)
      setActionError("No se pudo actualizar el medio de pago. Intente nuevamente.")
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleDeleteMedio = async (idMedio) => {
    const confirmDelete = window.confirm("¿Está seguro de eliminar este medio de pago?")
    if (!confirmDelete) return

    setActionLoadingId(idMedio)
    setActionError(null)
    try {
      await deleteMedioDePago(idMedio)
    } catch (error) {
      console.error("Error al eliminar medio:", error)
      
      const errorMessage = error?.response?.data?.message || error?.message || "No se pudo eliminar el medio de pago"
      
      if (errorMessage.includes("transacción") || errorMessage.includes("transaccion") || errorMessage.includes("pago")) {
        setActionError(`⚠️ ${errorMessage}. No se puede eliminar porque está en uso.`)
      } else {
        setActionError("No se pudo eliminar el medio de pago. Intente nuevamente.")
      }
    } finally {
      setActionLoadingId(null)
    }
  }

  if (!medioDePagoConDescripcion || medioDePagoConDescripcion.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
          px: 2,
        }}
      >
        <CreditCardIcon
          sx={{
            fontSize: 64,
            color: alpha("rgb(244, 147, 14)", 0.3),
            mb: 2,
          }}
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No hay medios de pago registrados
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Los medios de pago que agregues aparecerán aquí
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {actionError && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setActionError(null)}
        >
          {actionError}
        </Alert>
      )}
      
      {/* Buscador */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar por medio de pago, tipo o ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              backgroundColor: "#ffffff",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "rgb(244, 177, 14)" }} />
              </InputAdornment>
            ),
          }}
        />
        {searchTerm && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Mostrando {filteredData.length} de {medioDePagoConDescripcion.length} medio(s)
          </Typography>
        )}
      </Box>
      
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${alpha("#000", 0.1)}`,
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="tabla de medios de pago">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#5b5b5f",
              }}
            >
              <TableCell sx={{ fontWeight: 600, color: "#ffffff" }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#ffffff" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <PaymentIcon sx={{ mr: 1, fontSize: 20 }} />
                  Tipo de Medio de Pago
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#ffffff" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CreditCardIcon sx={{ mr: 1, fontSize: 20 }} />
                  Medio de Pago
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: "#ffffff" }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((medio) => (
              <TableRow
                key={medio.id}
                sx={{
                  "&:nth-of-type(even)": {
                    backgroundColor: "#F7F7F7",
                  },
                  "&:hover": {
                    backgroundColor: "#E3EAFD !important",
                    transition: "background 0.3s ease",
                  },
                  transition: "background 0.2s ease",
                }}
              >
                <TableCell>
                  <Chip
                    label={medio.id}
                    size="small"
                    sx={{
                      backgroundColor: alpha("#5b5b5f", 0.1),
                      color: "#5b5b5f",
                      fontWeight: 500,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={getTipoDescripcion(medio.idTipoMedioDePago)}
                    sx={{
                      backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                      color: "rgb(244, 177, 14)",
                      fontWeight: 500,
                      "& .MuiChip-label": {
                        px: 2,
                      },
                    }}
                    icon={<PaymentIcon sx={{ fontSize: 16 }} />}
                  />
                </TableCell>
                <TableCell component="th" scope="row">
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {medio.descripcion}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                    <Tooltip title="Editar medio de pago">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditModal(medio)}
                          disabled={actionLoadingId !== null}
                          sx={{
                            color: "rgb(244, 177, 14)",
                            bgcolor: alpha("rgb(244, 177, 14)", 0.1),
                            "&:hover": {
                              bgcolor: alpha("rgb(244, 177, 14)", 0.2),
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Eliminar medio de pago">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteMedio(medio.id)}
                          disabled={actionLoadingId !== null}
                          sx={{
                            color: "#d32f2f",
                            bgcolor: alpha("#d32f2f", 0.1),
                            "&:hover": {
                              bgcolor: alpha("#d32f2f", 0.2),
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredData.length > rowsPerPage && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
          sx={{
            borderTop: `1px solid ${alpha("#000", 0.1)}`,
            "& .MuiTablePagination-toolbar": {
              px: 2,
            },
          }}
        />
      )}

      {/* Modal de Edición */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 24,
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#5b5b5f",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <EditIcon />
          Editar Medio de Pago
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="edit-tipo-label">Tipo de Medio de Pago</InputLabel>
              <Select
                labelId="edit-tipo-label"
                value={editIdTipoMedioDePago}
                onChange={(e) => setEditIdTipoMedioDePago(e.target.value)}
                label="Tipo de Medio de Pago"
                disabled={actionLoadingId !== null}
                sx={{
                  borderRadius: 2,
                }}
              >
                {tipoMedioDePago.map((tipo) => (
                  <MenuItem key={tipo.id} value={tipo.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PaymentIcon sx={{ fontSize: 18, color: "rgb(244, 177, 14)" }} />
                      {tipo.descripcion}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Descripción del Medio de Pago"
              variant="outlined"
              fullWidth
              value={editDescripcion}
              onChange={(e) => setEditDescripcion(e.target.value)}
              disabled={actionLoadingId !== null}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CreditCardIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {actionError && (
              <Alert severity="error" onClose={() => setActionError(null)}>
                {actionError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCloseEditModal}
            disabled={actionLoadingId !== null}
            startIcon={<CloseIcon />}
            sx={{
              color: "#6b6b6b",
              "&:hover": {
                backgroundColor: alpha("#6b6b6b", 0.1),
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveEdit}
            disabled={actionLoadingId !== null}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: "#5b5b5f",
              "&:hover": {
                backgroundColor: alpha("#5b5b5f", 0.9),
              },
            }}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TablaMedioDePagoComponent
